import React, { useState } from "react";
import {
    TextField, Button, Grid, Tooltip, FormControl, InputLabel, Select,
    MenuItem, Checkbox, ListItemText, OutlinedInput, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Box, Chip, TableSortLabel,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { NotificationManager } from "react-notifications";
import { GetBottleCountReport } from "../../services/BottleOrdersService";
import CommonLoader from "../common/CommonLoader";
import { bottleOrderStatusList } from "../common/statusLists";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND_COLOR = [0, 83, 156];

const formatDisplayDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${m}/${day}/${y}`;
};

const generatePdf = ({ reportData, startDate, endDate, selectedStatuses, searchText }) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 36;

    // Header
    doc.setFillColor(...BRAND_COLOR);
    doc.rect(0, 0, pageWidth, 54, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Bottle Count Report — Date Range (Due Date)", pageWidth / 2, 24, { align: "center" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Kit Build Due Date: ${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`, pageWidth / 2, 40, { align: "center" });
    const statusLabel = selectedStatuses.length > 0 ? selectedStatuses.join(", ") : "All statuses";
    doc.text(`Statuses: ${statusLabel}`, pageWidth / 2, 52, { align: "center" });

    const totalCount = reportData.reduce((sum, r) => sum + r.count, 0);

    autoTable(doc, {
        startY: 70,
        margin: { left: marginX, right: marginX },
        head: [["Bottle Name", "Description", "Count"]],
        body: [
            ...reportData.map((r) => [r.bottleName, r.description || "", r.count]),
            ["Total", "", totalCount],
        ],
        headStyles: { fillColor: BRAND_COLOR, textColor: 255, fontStyle: "bold" },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        didParseCell: (data) => {
            if (data.row.index === reportData.length) {
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.fillColor = [220, 230, 245];
            }
        },
        columnStyles: { 2: { halign: "right" } },
    });

    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, doc.internal.pageSize.getHeight() - 16);

    const fileName = `bottle-count-report-${startDate}-to-${endDate}.pdf`;
    doc.save(fileName);
};

const BottleCountReport = () => {
    const today = new Date().toISOString().split("T")[0];
    const thirtyDaysOut = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(thirtyDaysOut);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    // null orderBy keeps the server order (count desc, then name)
    const [orderBy, setOrderBy] = useState(null);
    const [order, setOrder] = useState("asc");

    const handleSearch = async () => {
        if (!startDate || !endDate) {
            NotificationManager.error("Please provide both start and end dates.");
            return;
        }
        setLoading(true);
        try {
            const response = await GetBottleCountReport(startDate, endDate, selectedStatuses);
            setReportData(response.results || []);
        } catch {
            NotificationManager.error("Error running report.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };

    const handleStatusChange = (event) => {
        const val = event.target.value;
        setSelectedStatuses(typeof val === "string" ? val.split(",") : val);
    };

    const handleSaveReport = () => {
        if (!filteredData || filteredData.length === 0) {
            NotificationManager.warning("No data available to export.");
            return;
        }
        generatePdf({ reportData: sortedData, startDate, endDate, selectedStatuses, searchText });
    };

    const filteredData = reportData
        ? reportData.filter((r) =>
              !searchText || r.bottleName.toLowerCase().includes(searchText.toLowerCase())
          )
        : null;

    const handleRequestSort = (column) => {
        setOrder(orderBy === column && order === "asc" ? "desc" : "asc");
        setOrderBy(column);
    };

    const sortedData = filteredData && orderBy
        ? [...filteredData].sort((a, b) => {
              const cmp = orderBy === "count"
                  ? a.count - b.count
                  : (a[orderBy] || "").localeCompare(b[orderBy] || "", undefined, { sensitivity: "base", numeric: true });
              return order === "asc" ? cmp : -cmp;
          })
        : filteredData;

    const totalCount = filteredData ? filteredData.reduce((sum, r) => sum + r.count, 0) : 0;

    return (
        <div className="vlp-component-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="title">Bottle Count Report</span>
            </div>
            <hr />
            <div className="columns">
                <div className="column">
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <Tooltip title="Filter results by bottle name" arrow placement="top">
                                <TextField
                                    label="Search Bottle Name"
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    placeholder="Filter by bottle name..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Tooltip title="Kit Build Due Date from" arrow placement="top">
                                <TextField
                                    label="From Date"
                                    type="date"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Tooltip title="Kit Build Due Date to" arrow placement="top">
                                <TextField
                                    label="To Date"
                                    type="date"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl size="small" fullWidth variant="outlined">
                                <InputLabel>Status (all if none selected)</InputLabel>
                                <Select
                                    multiple
                                    value={selectedStatuses}
                                    onChange={handleStatusChange}
                                    input={<OutlinedInput label="Status (all if none selected)" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                                        </Box>
                                    )}
                                >
                                    {bottleOrderStatusList.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            <Checkbox checked={selectedStatuses.includes(s)} />
                                            <ListItemText primary={s} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                onClick={handleSearch}
                                style={{ height: "40px" }}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </div>
            </div>

            {filteredData !== null && (
                filteredData.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                        No bottles found for the selected criteria.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ mb: 1.5, mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {filteredData.length} bottle type{filteredData.length !== 1 ? "s" : ""}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total: <strong>{totalCount}</strong>
                            </Typography>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={handleSaveReport}
                                style={{ whiteSpace: "nowrap", marginLeft: "auto" }}
                            >
                                Save Report
                            </Button>
                        </Box>
                        <Paper variant="outlined">
                            <TableContainer>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {[
                                                { key: "bottleName", label: "Bottle Name", width: "40%" },
                                                { key: "description", label: "Description", width: "45%" },
                                                { key: "count", label: "Count", align: "right" },
                                            ].map((col) => (
                                                <TableCell
                                                    key={col.key}
                                                    align={col.align}
                                                    sx={{ fontWeight: 700, backgroundColor: "primary.main", color: "white", width: col.width }}
                                                >
                                                    <TableSortLabel
                                                        active={orderBy === col.key}
                                                        direction={orderBy === col.key ? order : "asc"}
                                                        onClick={() => handleRequestSort(col.key)}
                                                        sx={{
                                                            color: "white !important",
                                                            "& .MuiTableSortLabel-icon": { color: "white !important" },
                                                        }}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedData.map((row, idx) => (
                                            <TableRow
                                                key={row.bottleName}
                                                sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.03)" : "white" }}
                                            >
                                                <TableCell>{row.bottleName}</TableCell>
                                                <TableCell>{row.description}</TableCell>
                                                <TableCell align="right"><strong>{row.count}</strong></TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ backgroundColor: "#dce6f5" }}>
                                            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                            <TableCell />
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{totalCount}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )
            )}

            <CommonLoader loading={loading} />
        </div>
    );
};

export default BottleCountReport;
